import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useFormik } from 'formik'
import { format, isAfter, isBefore, parse, startOfDay, subDays } from 'date-fns'
import React, { useEffect, useMemo, useRef, useState, useEffectEvent } from 'react'
import { useLocation, useNavigate } from 'react-router'
import * as yup from 'yup'

import { useQueryClient } from '@tanstack/react-query'
import { referralSteerageProviders } from '@fireflyhealth/api'
import { useGetPerson } from '~/entities/person'
import {
  asReferralInsuranceAuthorizationStatus,
  AUTHORIZATION_STATUS_ACCEPTED,
  EditSteeragePayload,
  MEMBER_SERVICE_LEVEL,
  PRODUCT_TYPE,
  REFERRAL_INSURANCE_AUTHORIZATION_STATUSES,
  Steerage,
  SteerageProvider,
  SteerageProviderStatus,
  useEditSteerage,
  useEditSteerageProviderWaiverLevel,
  useReasonForException,
  useReferralDetails,
  useSteerage,
  useSteerageProviders,
  useToggleSteerageLock,
  type EditSteerageProviderWaiverLevelPayload,
  type ReferralInsuranceAuthorizationStatus,
} from '~/entities/steerage'
import { useStates } from '~/entities/state'
import { EditField } from '~/shared/ui'
import { formatApiDate, ISO_DATE_FORMAT, isValidIsoDate } from '~/shared/lib/date'
import { logEvent, SteerageEventPayload } from '~/shared/lib/events'
import SteerageProviderForm from './SteerageProviderForm'
import { BackgroundBlock } from './ReferralRequest'
import { SteerageProviderList as SteerageProviderListV4 } from './SteerageProviderList'
import NavigationServiceCategoryAutocomplete from './NavigationServiceCategoryAutocomplete'
import { getEffectivePriorAuthorization, getServiceCategoryError } from './utils'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { yupSiblingString } from '~/shared/lib/guards'

const MIN_NUMBER_OF_PROVIDERS_FOR_SELF_SERVICE = 3

const PROVIDER_TABS = {
  memberAppSearch: 'member app search' as const,
  addProvider: 'add provider' as const,
} as const

/**
 * Strict `yyyy-MM-dd` parse. Every caller gates on `isValidIsoDate` first, so an
 * unparseable value never reaches the comparisons below.
 */
const asIsoDate = (value: string) => parse(value, ISO_DATE_FORMAT, new Date())

const createValidationSchema = (isLocked: boolean, initialEffectiveFrom: string) => {
  const oneYearAgo = startOfDay(subDays(new Date(), 365))

  return yup.object().shape({
    effectiveFrom: yup
      .string()
      .nullable()
      .test('valid-date', 'Enter a valid date', v => !v || isValidIsoDate(v))
      .test('min-date', 'Cannot be more than 365 days in the past', v => {
        if (isLocked) return true
        if (!v || !isValidIsoDate(v)) return true
        if (v === initialEffectiveFrom) return true
        return !isBefore(asIsoDate(v), oneYearAgo)
      })
      .test('before-through', 'Must be on or before Effective Through', function (v) {
        if (isLocked) return true
        const through = yupSiblingString(this, 'effectiveThrough')
        if (!v || !through) return true
        if (!isValidIsoDate(v) || !isValidIsoDate(through)) return true
        return !isAfter(asIsoDate(v), asIsoDate(through))
      }),
    effectiveThrough: yup
      .string()
      .nullable()
      .test('valid-date', 'Enter a valid date', v => !v || isValidIsoDate(v))
      .test('after-from', 'Must be on or after Effective From', function (v) {
        if (isLocked) return true
        const from = yupSiblingString(this, 'effectiveFrom')
        if (!v || !from) return true
        if (!isValidIsoDate(v) || !isValidIsoDate(from)) return true
        return !isBefore(asIsoDate(v), asIsoDate(from))
      }),
    serviceQuantityValue: yup.number().min(1, 'Must be greater than 0'),
    schedulingDate: yup
      .string()
      .nullable()
      .test('valid-date', 'Enter a valid date', v => !v || isValidIsoDate(v)),

    // Navigation service category is required
    navigationServiceCategory: yup.mixed().nullable().required('Service Category is required'),
  })
}

export const ReferralDetails: React.FC<{
  steerageId: number
  personId: number | null
  // The case the user navigated from (set on the originating PatientCategorizedTodos
  // content_object), threaded down as a prop rather than smuggled onto the steerage cache object.
  activeCaseId: number | null
}> = props => {
  const client = useQueryClient()

  const { data: steerage, isPending: steerageIsLoading } = useSteerage(props.steerageId)
  const { data: steerageProviderList, isPending: steerageProvidersListIsLoading } =
    useSteerageProviders(props.steerageId)
  const { isPending: isTogglingSteerageLoading, mutate: toggleSteerageLock } =
    useToggleSteerageLock()
  const editSteerage = useEditSteerage()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: states } = useStates()
  // Populating the reason for exception list for provider
  const { data: validReasonsForException = [], isPending: reasonForExceptionLoading } =
    useReasonForException('manual')
  const isLocked = Boolean(steerage?.is_locked)
  const shouldShowWaiver =
    steerage?.member_service_level != MEMBER_SERVICE_LEVEL.CARE ||
    steerage?.waiver?.is_eligible == true
  const shouldShowReferralOnly =
    steerage?.member_service_level == MEMBER_SERVICE_LEVEL.CARE &&
    steerage?.waiver?.is_eligible != true
  const canToggleCareKey =
    steerage?.member_service_level == MEMBER_SERVICE_LEVEL.CARE_AND_COVERAGE ||
    steerage?.member_service_level == MEMBER_SERVICE_LEVEL.COVERAGE
  const { isLoading: referralDetailsIsLoading, specialtiesAndLocationTypes } = useReferralDetails()
  const [isWaiverToggleEnabled, setIsWaiverToggleEnabled] = useState(
    steerage?.waiver?.is_eligible ?? true,
  )

  const [showAddProviderForm, setShowAddProviderForm] = useState(false)
  const [showEditProviderForm, setShowEditProviderForm] = useState<number | null>(null)
  const [providerSelectionTab, setProviderSelectionTab] = useState<string>()

  const { isPending: isWaiverLevelLoading, mutateAsync: handleEditSteerageProviderWaiverLevel } =
    useEditSteerageProviderWaiverLevel()

  // Read-only information at the top of the section
  const { data: person } = useGetPerson({ personId: props.personId })

  // Disable the add exception button if there are providers under review
  const isAddExceptionDisabled =
    steerage?.steerage_providers?.some(provider => provider.status === 'under_review') ?? false

  // When there are providers automatically added to a Steerage, they should
  // only render in this list if they have been approved by a navigator.
  const filteredSteerageProviderList = (steerageProviderList ?? []).filter(p => {
    return p.status === SteerageProviderStatus.Accepted
  })

  const productType = person?.insurance_info?.product_type ?? null
  // The backend stores the plan type lowercase; the insurance block has always shown it uppercased.
  const productTypeLabel = productType?.toUpperCase() ?? 'None'
  const planName = person?.insurance_info?.insurance_plan?.name ?? 'None'
  const payerName = person?.insurance_info?.insurance_payer?.name ?? 'None'
  const pcpName = person?.insurance_info?.pcp_name ?? 'None'

  const hasPriorAuth = Boolean(steerage && getEffectivePriorAuthorization(steerage))

  // Ensure no default/invalid date is shown; sanitize API value to empty if invalid
  const initialSchedulingDate = useMemo(() => {
    const v = steerage?.scheduling_date
    return isValidIsoDate(v) ? v : ''
  }, [steerage?.scheduling_date])

  const initialEffectiveFrom = steerage?.effective_from ?? ''

  const validationSchema = useMemo(() => {
    return createValidationSchema(isLocked, initialEffectiveFrom)
  }, [isLocked, initialEffectiveFrom])

  /**
   * Three cases to override initial waiver from True:
   *  1. If steerage is locked and waiver not created then use as False
   *  2. If steerage was saved as draft (Description is present) and waiver was not created then use as False
   *  3. If waiver is present then use the is_active value
   * As the care key is only created when we save the care key details, we need to handle default values based on the state of steerage
   * If steerage is locked then use the care key else keep the care key as active by default
   */
  // Hoisted so `onFormSubmit` can name this shape directly: formik now takes its submit promise,
  // so a `typeof formik.values` parameter would make formik's own type circular.
  const initialValues = {
    schedulingDate: initialSchedulingDate,
    authorizationNumber:
      steerage?.referral?.referral_insurance_authorization?.authorization_number ?? '',
    insuranceAuthorizationRequired: steerage?.referral?.insurance_authorization_required ?? null,
    priorAuthorizationRequired: steerage?.prior_authorization_required ?? null,
    insuranceAuthorizationStatus:
      steerage?.referral?.referral_insurance_authorization?.status ?? '',
    preferredProviderOrFacility: steerage?.preferred_provider_or_facility ?? '',

    description: steerage?.description ?? '',
    navigationServiceCategory: steerage?.navigation_service_category ?? null,
    serviceQuantityUnit: steerage?.service_quantity?.unit ?? '',
    serviceQuantityValue: steerage?.service_quantity?.value ?? '',
    effectiveFrom: steerage?.effective_from ?? '',
    effectiveThrough: steerage?.effective_through ?? '',
    isWaiverActive:
      steerage?.waiver?.is_eligible ?? !(steerage?.recommendation_sent_at || steerage?.description),
  }

  const formik = useFormik({
    validationSchema: validationSchema,
    enableReinitialize: true,
    initialValues,
    onSubmit: values => onFormSubmit(values, false),
  })

  // False positive: ESLint incorrectly flags these hooks as conditional
  // All hooks are called unconditionally at the top level of the component
  const isMemberSelfServiceDisabled: boolean = useMemo(() => {
    const allSteerageProviders = steerageProviderList ?? []

    // If there are fewer than 3 recommendations, disable self service
    if (
      allSteerageProviders &&
      allSteerageProviders.length < MIN_NUMBER_OF_PROVIDERS_FOR_SELF_SERVICE
    )
      return true

    // If any of the recommended or added providers are partners, disable self service
    if (allSteerageProviders?.some(provider => provider.partnership)) return true

    // If self service has been disabled (providers previously added by navigator), disable self service now
    if (steerage?.is_self_service_enabled === false) return true

    // If provider has been accepted and if we have not sent to member yet, then the navigator added the provider
    // so disable self service button
    if (
      filteredSteerageProviderList &&
      filteredSteerageProviderList.length > 0 &&
      !steerage?.recommendation_sent_at
    )
      return true

    // Only enable the self serve for eligible service category
    // Check if the selected navigation service category is self-service eligible
    const category = formik.values.navigationServiceCategory
    if (!category) {
      return true
    }
    const isSelfServiceEligible = category.is_self_service_eligible === true
    if (!isSelfServiceEligible) {
      return true
    }

    return false
    // Keyed on the values read above. `is_self_service_enabled` was missing, so a steerage
    // whose self-service was turned off could keep the button enabled until something else
    // invalidated this memo; `contract_name` was listed but is read nowhere in this file.
  }, [
    filteredSteerageProviderList,
    steerageProviderList,
    steerage?.recommendation_sent_at,
    steerage?.is_self_service_enabled,
    formik.values,
  ])

  // The tab defaults from whether self-service is disabled, and again whenever that changes;
  // the navigator can switch tabs in between.
  const [tabDefaultedForSelfServiceDisabled, setTabDefaultedForSelfServiceDisabled] =
    useState<boolean>()
  if (isMemberSelfServiceDisabled !== tabDefaultedForSelfServiceDisabled) {
    setTabDefaultedForSelfServiceDisabled(isMemberSelfServiceDisabled)
    setProviderSelectionTab(
      isMemberSelfServiceDisabled ? PROVIDER_TABS.addProvider : PROVIDER_TABS.memberAppSearch,
    )
  }

  // The schema depends on the provider tab, so a tab change re-validates — but only for
  // a tab the operator clicked. The tab is also reassigned automatically whenever async
  // data moves `isMemberSelfServiceDisabled`, which can land mid-edit and re-validate
  // pre-edit values; formik does not cancel superseded runs, so that stale pass can
  // resolve last and wipe a real error. Automatic reassignments need no re-validation of
  // their own: formik re-validates on the next change, blur and submit.
  const providerSelectionTabPickedByUser = useRef(false)
  const revalidateForPickedTab = useEffectEvent(() => {
    void formik.validateForm()
  })
  useEffect(() => {
    if (!providerSelectionTabPickedByUser.current) {
      return
    }
    providerSelectionTabPickedByUser.current = false

    revalidateForPickedTab()
  }, [providerSelectionTab])

  // False positive: ESLint incorrectly flags these hooks as conditional
  // All hooks are called unconditionally at the top level of the component
  const isLockingDisabled: boolean = useMemo(() => {
    // Disable the lock button until all required details are added

    // Steerage Validations
    if (!steerage?.description && !formik.values.description) {
      return true
    }

    // If a type of self service is not selected
    if (!steerage?.is_locked && !providerSelectionTab) {
      return true
    }

    // PA requirement is required for all steerages
    if (
      formik.values.priorAuthorizationRequired === null &&
      steerage?.prior_authorization_required === null
    ) {
      return true
    }
    // Navigation service category is required
    if (!formik.values.navigationServiceCategory) {
      return true
    }

    // Care Key Checks -> If Care Key Toggle is On
    if (formik.values.isWaiverActive && shouldShowWaiver) {
      // Either both unit and quantity should be present or none
      if (!formik.values.serviceQuantityUnit && formik.values.serviceQuantityValue) {
        return true
      }
      if (formik.values.serviceQuantityUnit && !formik.values.serviceQuantityValue) {
        return true
      }

      // effective dates are only required in case of waiver
      if (!formik.values?.effectiveFrom) {
        return true
      }
      if (!formik.values?.effectiveThrough) {
        return true
      }
    }

    // Care only checks -> make sure insurance auth details are present
    if (
      productType != PRODUCT_TYPE.PPO &&
      shouldShowReferralOnly &&
      steerage?.referral?.insurance_authorization_required === null &&
      !formik.values.insuranceAuthorizationRequired
    ) {
      return true
    }

    // If we do not have automated recommendations (without partners) and we do not have at least one accepted provider
    // then disable locking
    const allSteerageProviders = steerageProviderList ?? []
    if (
      providerSelectionTab === PROVIDER_TABS.memberAppSearch &&
      allSteerageProviders &&
      (allSteerageProviders.length < MIN_NUMBER_OF_PROVIDERS_FOR_SELF_SERVICE ||
        allSteerageProviders.some(provider => provider.partnership))
    )
      return true
    if (
      providerSelectionTab !== PROVIDER_TABS.memberAppSearch &&
      filteredSteerageProviderList &&
      filteredSteerageProviderList.length < 1
    ) {
      return true
    }

    // When PA is required, the originator ID lives on the provider:
    // - self-service: no originator ID required, allow lock (provider count already checked above)
    // - non-self-service, 1 provider: that provider must have an originator ID
    // - non-self-service, 2+ providers: the member-selected provider must have an originator ID
    if (
      formik.values.priorAuthorizationRequired === true &&
      providerSelectionTab !== PROVIDER_TABS.memberAppSearch
    ) {
      const hasOriginatorId = (provider: SteerageProvider) =>
        Boolean(
          provider.prior_authorization_system_id ||
          provider.prior_authorization?.originator_system_id,
        )

      const [onlyProvider] = filteredSteerageProviderList
      if (filteredSteerageProviderList.length === 1 && onlyProvider) {
        if (!hasOriginatorId(onlyProvider)) {
          return true
        }
      } else if (filteredSteerageProviderList.length > 1) {
        const memberSelectedProvider = filteredSteerageProviderList.find(
          provider => provider.member_selected_at !== null,
        )
        // No provider has been selected by the member yet
        if (!memberSelectedProvider) {
          return true
        }
        if (!hasOriginatorId(memberSelectedProvider)) {
          return true
        }
      }
    }

    return false
  }, [
    formik.values,
    steerage,
    filteredSteerageProviderList,
    steerageProviderList,
    providerSelectionTab,
    shouldShowWaiver,
    productType,
    shouldShowReferralOnly,
  ])

  // Something about the number of pipes in determining a boolean causes the linter to fail,
  // however breaking it out as such fixes the lint issue.
  let isLoading = false
  if (steerageIsLoading) isLoading = true
  if (isTogglingSteerageLoading) isLoading = true
  if (steerageProvidersListIsLoading) isLoading = true
  if (editSteerage.isPending) isLoading = true
  if (isWaiverLevelLoading) isLoading = true
  if (referralDetailsIsLoading) isLoading = true

  const isDisabled = isLoading || isLocked

  /**
   * The scheduling date should be disabled under the following conditions:
   * 1. There are no accepted Steerage Providers added to the Steerage
   * 2. There's more than one Steerage Provider but none of them have been selected
   */
  const noProvidersAreAdded = filteredSteerageProviderList.length === 0
  const noProviderIsSelected =
    filteredSteerageProviderList.length > 1 &&
    filteredSteerageProviderList.every(p => p.member_selected_at === null)
  const isSchedulingDateDisabled = isDisabled || noProvidersAreAdded || noProviderIsSelected
  const isSteerageLockedOnce = steerage?.recommendation_sent_at ? true : false

  // HTML-level minDate floors that mirror the Yup schema so the picker only
  // grays out dates the schema would also reject.
  // - effectiveFrom: schema allows backdating up to 365 days, but skips that
  //   floor when the steerage is locked or when the initial value is older than
  //   365 days (i.e. was set before this rule existed).
  // - effectiveThrough: schema enforces >= effectiveFrom (after-from), so the
  //   picker mirrors that floor.
  const oneYearAgoIso = format(subDays(new Date(), 365), ISO_DATE_FORMAT)
  const initialEffectiveFromIsOlderThan365Days =
    isValidIsoDate(initialEffectiveFrom) &&
    isBefore(asIsoDate(initialEffectiveFrom), startOfDay(subDays(new Date(), 365)))
  const effectiveFromMinDate =
    isLocked || initialEffectiveFromIsOlderThan365Days ? undefined : oneYearAgoIso
  const effectiveThroughMinDate = isLocked
    ? undefined
    : isValidIsoDate(formik.values.effectiveFrom)
      ? formik.values.effectiveFrom
      : undefined

  const handleLockButtonOnClick = () =>
    fireAndForget('[Steerage] lock referral', () => onFormSubmit(formik.values, true))

  const handleSaveAsDraftButtonOnClick = () =>
    fireAndForget('[Steerage] save referral draft', () => onFormSubmit(formik.values, false))

  const onClickOfAddProvider = () => {
    // This is used when we click add provider button for adding steerage provider via provider search
    // if the form values of care key are not changed then no need to update it else call submit form
    if (formik.dirty) {
      formik.handleSubmit()
    }
    void logEvent<SteerageEventPayload>('STEERAGE_INITIATE_ADD_PROVIDER_WORKFLOW', {
      steerageId: props.steerageId,
    })
  }

  // Persist any pending steerage form edits before a child triggers a steerage
  // refetch (e.g. per-provider PA save), so re-initialization doesn't wipe them.
  // Calls onFormSubmit directly because formik's submitForm() resolves before
  // the async onSubmit handler awaits the underlying mutation.
  const handleSaveSteerageBeforeChildMutation = async () => {
    if (formik.dirty) {
      await onFormSubmit(formik.values, false)
    }
  }

  let labelForLock = 'Send to Member'
  if (steerage?.recommendation_sent_at) labelForLock = 'Resend to Member'

  type ServiceQuantityUnit = NonNullable<EditSteeragePayload['service_quantity']>['unit']

  const onFormSubmit = async (values: typeof initialValues, shouldLock: boolean): Promise<void> => {
    // When the steerage form is dirty and shouldLock is False - It means navigator tried to save steerage details
    // Then just save the steerage details
    let authorizationNumber: string | null = values.authorizationNumber
    let authorizationStatus: ReferralInsuranceAuthorizationStatus | null =
      asReferralInsuranceAuthorizationStatus(values.insuranceAuthorizationStatus)
    const insuranceAuthRequired = Boolean(values.insuranceAuthorizationRequired)
    if (!insuranceAuthRequired) {
      authorizationNumber = null
      authorizationStatus = null
    }
    const payload: EditSteeragePayload = {
      description: values.description,
      id: props.steerageId,
      scheduling_date: values.schedulingDate != '' ? values.schedulingDate : null,
    }
    payload['navigation_service_category_id'] = values.navigationServiceCategory?.id ?? null
    // If care only, add the referral insurance details to the payload
    if (shouldShowReferralOnly) {
      payload['referral'] = {
        insurance_authorization_required: insuranceAuthRequired,
        referral_insurance_authorization: {
          authorization_number: authorizationNumber,
          status: authorizationStatus,
        },
      }
    } else {
      const quantityValue = values['serviceQuantityValue']
      payload['service_quantity'] = {
        value: quantityValue === '' || quantityValue == null ? null : Number(quantityValue),
        unit: (values['serviceQuantityUnit'] || null) as ServiceQuantityUnit,
      }
      payload['effective_from'] = values.effectiveFrom || null
      payload['effective_through'] = values.effectiveThrough || null
      payload['waiver'] = {
        is_eligible: values.isWaiverActive,
      }
    }
    if (values.priorAuthorizationRequired != null) {
      const priorAuthorizationRequired = Boolean(values.priorAuthorizationRequired)
      // The originator ID lives on each provider, so this payload only carries whether an auth is
      // required at all.
      payload['prior_authorization_required'] = priorAuthorizationRequired
    }
    const updateWaiverLevelPromises = filteredSteerageProviderList
      .filter(provider => !provider.waiver_level)
      .map(provider => {
        let waiverLevel: EditSteerageProviderWaiverLevelPayload['waiver_level'] = 'Not Applicable'

        if (provider.recommendation_status === 'RECOMMENDED') {
          waiverLevel = provider.npi ? 'Provider' : 'Facility'
        }

        const payload: EditSteerageProviderWaiverLevelPayload = {
          id: provider.id,
          steerage_id: props.steerageId,
          waiver_level: waiverLevel,
        }

        return handleEditSteerageProviderWaiverLevel(payload)
      })

    // Wait for all waiver level updates to complete
    await Promise.all(updateWaiverLevelPromises)

    await editSteerage.mutateAsync(payload, {
      onSuccess: () => formik.resetForm(),
      onError: (error: unknown) => {
        const serviceCategoryError = getServiceCategoryError(error)
        if (serviceCategoryError) {
          void formik.setFieldTouched('navigationServiceCategory', true, false)
          formik.setFieldError('navigationServiceCategory', serviceCategoryError)
        }
      },
    })

    // Invalidate provider queries so we get fresh data
    await client.cancelQueries({
      queryKey: referralSteerageProviders.listQueryKey({ steerage_id: props.steerageId ?? 0 }),
    })
    await client.invalidateQueries({
      queryKey: referralSteerageProviders.listQueryKey({ steerage_id: props.steerageId ?? 0 }),
    })

    // If locking is needed, do it now
    if (shouldLock) {
      toggleSteerageLock({
        steerageId: props.steerageId,
        isLocked,
      })
    }
  }

  const handleExceptionOnClick = () => {
    // show the create form and hide the edit form
    setShowAddProviderForm(showAddProviderForm == true ? false : true)
    setShowEditProviderForm(null) // setting value to null for hiding the edit form
    if (steerage) {
      void logEvent<SteerageEventPayload>('STEERAGE_ADD_EXCEPTION', {
        steerageId: steerage.id,
      })
    }
  }

  const hideAddUpdateProviderForm = () => {
    setShowAddProviderForm(false)
    setShowEditProviderForm(null)
  }

  const addProviderViaSearch = () => {
    onClickOfAddProvider()
    const path = location.pathname
    const basePath = path.substring(0, path.lastIndexOf('/'))

    // Redirect it to provider search and passing Case ID, so that we can link back to case
    // patients/<user_id>/todos?caseId=<case_id>.
    // Label the case by its category + priority (e.g. "(ENT) Otolaryngology | STANDARD" for a
    // specialty group, or "Physical Therapy | STANDARD" for a service/location type), falling
    // back to the free-text description and finally the created date.
    const specialtyGroupLabel = specialtiesAndLocationTypes.find(
      option => option.source === 'SpecialtyGroup' && option.id === steerage?.specialty_group,
    )?.label
    const locationTypeOption = specialtiesAndLocationTypes.find(
      option => option.source === 'LocationType' && option.id === steerage?.location_type,
    )
    const categoryLabel = specialtyGroupLabel || locationTypeOption?.label
    const description =
      steerage?.description ||
      [categoryLabel, steerage?.priority].filter(Boolean).join(' | ') ||
      (steerage?.created_at ? formatApiDate(steerage.created_at, 'MM/dd/yyyy') : '')

    const steerageId = steerage ? steerage?.id : null

    const searchParams = new URLSearchParams()
    searchParams.set('caseDescription', description ?? '')
    searchParams.set('steerage_id', steerageId?.toString() ?? '')
    if (steerage?.specialty_group) {
      searchParams.set('specialty_group', steerage.specialty_group.toString())
      if (steerage?.clinical_focus_areas?.length) {
        // If there are clinical focus areas then pass them as well
        searchParams.set(
          'clinical_focus_area_uids',
          JSON.stringify(steerage.clinical_focus_areas.map(area => area.id)),
        )
      }
    } else if (steerage?.services) {
      // If the services are mapped with the CPT code then navigate to procedure tab.
      const services = steerage.services
      for (const service of services) {
        const cptCodes = service?.cpt_codes
        if (cptCodes && cptCodes.length > 0) {
          // Take the first cpt code
          searchParams.set(
            'cptCode',
            cptCodes[0]?.display ? JSON.stringify(cptCodes[0]?.display) : '',
          )
          break
        }
      }
    }

    if (steerage?.cases != null && steerage.cases.length > 0) {
      const caseId = props.activeCaseId ?? null
      searchParams.set('caseId', caseId?.toString() ?? '')
    }

    if (steerage?.location_type && !referralDetailsIsLoading) {
      if (locationTypeOption?.place_of_service) {
        const placeOfServiceCodes = locationTypeOption.place_of_service.map(pos => pos.code)
        if (placeOfServiceCodes.length > 0) {
          searchParams.set('place_of_service_codes', JSON.stringify(placeOfServiceCodes))
        }
      }
    }

    void navigate(`${basePath}/providersearch?` + searchParams.toString())
  }

  // Don't render anything if steerage is not available and not loading
  if (!steerage && !isLoading) {
    return null
  }

  return (
    <Box sx={{ mx: 1, mt: -2 }}>
      <Box>
        <Box>
          {shouldShowWaiver ? (
            <BackgroundBlock>
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontWeight: 500 }}>Care Key</Typography>
                <Grid container sx={{ mt: 1, mb: 1 }}>
                  {canToggleCareKey && (
                    <Box sx={{ ml: 1 }}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              name="isWaiverActive"
                              onClick={() =>
                                setIsWaiverToggleEnabled(!formik.values?.isWaiverActive)
                              }
                              onChange={formik.handleChange}
                              edge="start"
                              checked={formik.values?.isWaiverActive}
                              color="primary"
                              disabled={
                                isDisabled ||
                                hasPriorAuth ||
                                steerage?.recommendation_sent_at != null
                              }
                            />
                          }
                          label="Service is eligible"
                        />
                      </FormGroup>
                    </Box>
                  )}
                </Grid>
                {formik.values?.isWaiverActive && (
                  <Box>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <EditField
                        error={Boolean(formik.errors.effectiveFrom)}
                        helperText={formik.errors.effectiveFrom}
                        label="Effective From"
                        type="date"
                        onChange={formik.handleChange}
                        value={formik.values.effectiveFrom}
                        name="effectiveFrom"
                        disabled={isDisabled}
                        minDate={effectiveFromMinDate}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                      <EditField
                        error={Boolean(formik.errors.effectiveThrough)}
                        helperText={formik.errors.effectiveThrough}
                        slotProps={{ inputLabel: { shrink: true } }}
                        label="Effective Through"
                        type="date"
                        onChange={formik.handleChange}
                        value={formik.values.effectiveThrough}
                        name="effectiveThrough"
                        disabled={isDisabled}
                        // Mirrors the after-from schema rule.
                        minDate={effectiveThroughMinDate}
                      />
                    </Grid>
                    <Grid container spacing={2} size={6} sx={{ mt: 1 }}>
                      <EditField
                        required={!!formik.values.serviceQuantityUnit}
                        helperText={formik.errors.serviceQuantityValue}
                        error={Boolean(formik.errors.serviceQuantityValue)}
                        label="Quantity"
                        type="number"
                        name="serviceQuantityValue"
                        onChange={formik.handleChange}
                        value={formik.values.serviceQuantityValue}
                        disabled={isDisabled}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />

                      <EditField
                        required={!!formik.values.serviceQuantityValue}
                        label="Unit"
                        name="serviceQuantityUnit"
                        onChange={formik.handleChange}
                        value={formik.values.serviceQuantityUnit}
                        select
                        disabled={isDisabled}
                        slotProps={{ inputLabel: { shrink: true }, select: { native: true } }}
                      >
                        <option value=""></option>
                        <option key="days" value="days">
                          Days
                        </option>
                        <option key="units" value="units">
                          Units
                        </option>
                        <option key="visits" value="visits">
                          Visits
                        </option>
                      </EditField>
                    </Grid>
                  </Box>
                )}
              </Box>
            </BackgroundBlock>
          ) : null}
          <BackgroundBlock>
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: '500' }}>Member-Facing</Typography>
            </Box>
            <Grid container spacing={2} sx={{ alignItems: 'flex-end' }}>
              <Grid size={6}>
                <TextField
                  disabled={isDisabled}
                  fullWidth
                  required
                  label="Description"
                  name="description"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  variant="standard"
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <NavigationServiceCategoryAutocomplete
                  disabled={isDisabled}
                  selectedCategory={formik.values.navigationServiceCategory ?? null}
                  required
                  error={Boolean(
                    formik.touched.navigationServiceCategory &&
                    formik.errors.navigationServiceCategory,
                  )}
                  helperText={
                    formik.touched.navigationServiceCategory
                      ? formik.errors.navigationServiceCategory
                      : undefined
                  }
                  onBlur={() => void formik.setFieldTouched('navigationServiceCategory', true)}
                  onChange={value => {
                    // value can be a NavigationServiceCategory or null
                    void formik.setFieldValue('navigationServiceCategory', value)
                  }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <EditField
                error={Boolean(formik.errors.schedulingDate)}
                helperText={formik.errors.schedulingDate}
                disabled={isSchedulingDateDisabled}
                label="Scheduled date"
                type="date"
                name="schedulingDate"
                value={formik.values.schedulingDate}
                onChange={e => {
                  void formik.setFieldValue('schedulingDate', e.target.value, false)
                }}
                onBlur={() => void formik.setFieldTouched('schedulingDate', true, true)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
          </BackgroundBlock>
          <BackgroundBlock>
            <Grid
              container
              rowSpacing={steerage?.is_locked ? 1 : 3}
              columnSpacing={2}
              sx={{ mt: steerage?.is_locked ? 0 : -2 }}
            >
              <Grid size={12}>
                <Typography sx={{ fontSize: 16, fontWeight: '500' }}>
                  Prior Authorization
                </Typography>
              </Grid>
              <Grid size={6} sx={{ mt: -1 }}>
                <FormControl fullWidth>
                  <InputLabel required sx={{ ml: -2 }}>
                    Authorization required
                  </InputLabel>
                  <Select<'' | number>
                    label="Authorization Required"
                    disabled={isLoading || isDisabled}
                    onChange={e => {
                      const value = e.target.value
                      void formik.setFieldValue(
                        'priorAuthorizationRequired',
                        value === '' ? null : Boolean(Number(value)),
                      )
                    }}
                    value={
                      formik.values.priorAuthorizationRequired === null
                        ? ''
                        : Number(formik.values.priorAuthorizationRequired)
                    }
                    name="priorAuthorizationRequired"
                    variant="standard"
                    fullWidth
                    required
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value={1}>Yes</MenuItem>
                    <MenuItem value={0}>No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formik.values.priorAuthorizationRequired === true && (
                <Grid size={12}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    If this is not a self-search request, only one provider is allowed to be sent to
                    a member if prior auth is required.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </BackgroundBlock>
          <BackgroundBlock>
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: '500' }}>Providers</Typography>
              <Box sx={{ alignSelf: 'center' }}>
                {isMemberSelfServiceDisabled && (
                  <Typography sx={{ pt: 1, pb: 1 }}>
                    Member self-search is not yet available for all requests and member types
                  </Typography>
                )}
                <RadioGroup
                  color="primary"
                  value={providerSelectionTab}
                  key={providerSelectionTab}
                  row
                  onChange={event => {
                    providerSelectionTabPickedByUser.current = true
                    setProviderSelectionTab(event.target.value)
                  }}
                  aria-label="Provider Selection"
                >
                  <FormControlLabel
                    value={PROVIDER_TABS.memberAppSearch}
                    control={<Radio />}
                    label="Member Self-Search"
                    disabled={isMemberSelfServiceDisabled || isLocked}
                  />
                  <FormControlLabel
                    value={PROVIDER_TABS.addProvider}
                    control={<Radio />}
                    label="Add Provider"
                    disabled={isLocked}
                  />
                </RadioGroup>
              </Box>
              <SteerageProviderListV4
                steerage={steerage as Steerage}
                steerageProviders={filteredSteerageProviderList}
                showEditProviderForm={showEditProviderForm}
                showAddProviderForm={showAddProviderForm}
                setShowAddProviderForm={setShowAddProviderForm}
                setShowEditProviderForm={setShowEditProviderForm}
                validReasonsForException={validReasonsForException}
                isWaiverToggleEnabled={isWaiverToggleEnabled}
                priorAuthorizationRequired={formik.values.priorAuthorizationRequired === true}
                onSaveSteerage={handleSaveSteerageBeforeChildMutation}
              />
              {steerage &&
                !steerage?.is_locked &&
                showAddProviderForm &&
                !reasonForExceptionLoading && (
                  <Box sx={{ mt: 2, fontWeight: '400' }}>
                    <SteerageProviderForm
                      steerage={steerage}
                      steerageProviderCreateForm={true}
                      steerageProvider={null}
                      onCancel={hideAddUpdateProviderForm}
                      reasonForExceptionList={validReasonsForException}
                      states={states ?? []}
                    />
                  </Box>
                )}
              {providerSelectionTab === PROVIDER_TABS.addProvider && (
                <Box>
                  <Box sx={{ mt: 1, ml: -1 }}>
                    <Button
                      component="span"
                      title="Add Provider"
                      onClick={addProviderViaSearch}
                      disabled={isDisabled}
                    >
                      + Add provider
                    </Button>
                  </Box>
                  <Box sx={{ mt: 1, ml: -1 }}>
                    <Button
                      component="span"
                      title="Add Exception"
                      onClick={handleExceptionOnClick}
                      disabled={isAddExceptionDisabled || isDisabled}
                    >
                      + Add exception
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </BackgroundBlock>
          {productType != PRODUCT_TYPE.PPO && shouldShowReferralOnly && (
            <BackgroundBlock>
              <Grid container rowSpacing={steerage?.is_locked ? 1 : 3} sx={{ mt: -1 }}>
                <Grid size={12}>
                  <Typography sx={{ fontWeight: '500' }}>Insurance Information</Typography>
                </Grid>
                <Grid size={6}>
                  <FormHelperText>Payer Name</FormHelperText>
                  <Typography>{payerName}</Typography>
                </Grid>
                <Grid size={6}>
                  <FormHelperText>Plan Name</FormHelperText>
                  <Typography>{planName}</Typography>
                </Grid>
                <Grid size={6}>
                  <FormHelperText>Plan Type</FormHelperText>
                  <Typography>{productTypeLabel}</Typography>
                </Grid>
                <Grid size={6}>
                  <FormHelperText>Insurance PCP</FormHelperText>
                  <Typography>{pcpName}</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography sx={{ fontWeight: '500' }}>Insurance Authorization</Typography>
                </Grid>
                <Grid size={6}>
                  <FormControl fullWidth>
                    <InputLabel required sx={{ ml: -2 }}>
                      Authorization required
                    </InputLabel>
                    <Select<'' | number>
                      label="Authorization Required"
                      disabled={isLoading || isDisabled}
                      onChange={e => {
                        const value = e.target.value
                        void formik.setFieldValue(
                          'insuranceAuthorizationRequired',
                          value === '' ? null : Boolean(Number(value)),
                        )
                      }}
                      value={
                        formik.values.insuranceAuthorizationRequired === null
                          ? ''
                          : Number(formik.values.insuranceAuthorizationRequired)
                      }
                      name="insuranceAuthorizationRequired"
                      variant="standard"
                      fullWidth
                      required
                    >
                      <MenuItem value="">Select...</MenuItem>
                      <MenuItem value={1}>Yes</MenuItem>
                      <MenuItem value={0}>No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {formik.values.insuranceAuthorizationRequired === true ? (
                  <>
                    <Grid size={12}>
                      <FormHelperText>ICD-10 Code(s)</FormHelperText>
                      {!steerage?.referral?.referral_insurance_auth_icd_codes?.length ? (
                        <Typography sx={{ color: 'text.secondary' }}>No codes attached</Typography>
                      ) : null}
                      {steerage?.referral?.referral_insurance_auth_icd_codes?.map(icdcode => {
                        return (
                          <Typography key={icdcode.code} sx={{ mb: 0.5 }}>
                            {icdcode?.code + ' ' + icdcode?.description}
                          </Typography>
                        )
                      })}
                    </Grid>
                    <Grid size={6} sx={{ pr: 1 }}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ ml: -2 }}>Authorization status</InputLabel>
                        <Select
                          label="Authorization Status"
                          disabled={isLoading || isDisabled}
                          onChange={e => {
                            void formik.setFieldValue(
                              'insuranceAuthorizationStatus',
                              e.target.value,
                            )
                          }}
                          value={formik.values.insuranceAuthorizationStatus}
                          name="insuranceAuthorizationStatus"
                          variant="standard"
                          fullWidth
                        >
                          {REFERRAL_INSURANCE_AUTHORIZATION_STATUSES.map(status => (
                            <MenuItem value={status} key={status}>
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        disabled={isLoading || isDisabled}
                        fullWidth
                        label="Authorization Number"
                        name="authorizationNumber"
                        onChange={formik.handleChange}
                        value={formik.values.authorizationNumber}
                        variant="standard"
                        required={
                          formik.values.insuranceAuthorizationStatus ===
                          AUTHORIZATION_STATUS_ACCEPTED
                        }
                      />
                    </Grid>
                  </>
                ) : null}
              </Grid>
            </BackgroundBlock>
          )}
          <Grid container sx={{ mt: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
            <Grid>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {!isSteerageLockedOnce ? (
                  <Button
                    variant="text"
                    onClick={handleSaveAsDraftButtonOnClick}
                    disabled={isDisabled}
                    endIcon={isLoading ? <CircularProgress size="1em" color="inherit" /> : null}
                    sx={{ marginRight: 1 }}
                  >
                    Save as Draft
                  </Button>
                ) : null}
                {!isLocked && (
                  <Button
                    onClick={handleLockButtonOnClick}
                    variant="outlined"
                    disabled={isLoading || isLockingDisabled}
                  >
                    {labelForLock}
                  </Button>
                )}
                {isLocked && (
                  <Button
                    onClick={handleLockButtonOnClick}
                    variant="outlined"
                    disabled={isLoading}
                    endIcon={isLoading ? <CircularProgress size="1em" color="inherit" /> : null}
                  >
                    Unlock
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}
